import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { useEffect, useRef } from 'react';
import type { Space } from '@/features/spaces/interfaces/space.interface';
import conf from '@/utils/conf';
import {
  acceptInvite,
  addGroupUsers,
  cancelInvitation,
  createGroup,
  createSpace,
  deleteGroup,
  deleteGroupUser,
  deleteSpace,
  fetchSpaceUsers,
  getAllNotifications,
  getAllSpaces,
  getGroupUsers,
  getSpaceById,
  getSpaceGroups,
  inviteUser,
  removeSpaceUser,
  removeUserFromGroup,
  type SpaceUser,
  updateGroup,
  updateSpace,
  updateSpaceUser,
} from './spaces.api';

type QueryHookOptions = {
  staleTime?: number;
};

// Query Keys Factory
const spacesQueryKeys = {
  all: ['spaces'] as const,
  space: (id?: string) => ['space', id] as const,
  users: (spaceId: string | undefined) => ['space-users', spaceId] as const,
  invoker: (spaceId: string | undefined) => ['space-invoker', spaceId] as const,
  notifications: ['notifications'] as const,
  groups: (spaceId: string | undefined) => ['space-groups', spaceId] as const,
  groupUsers: (groupId: string) => ['group-users', groupId] as const,
} as const;

/**
 * Hook to fetch all spaces
 */
export const useSpaces = (options: QueryHookOptions = {}) => {
  const { staleTime = 60000 } = options;

  return useQuery<Space[]>({
    queryKey: spacesQueryKeys.all,
    queryFn: getAllSpaces,
    staleTime,
    enabled: true,
  });
};

/**
 * Hook to fetch a space by ID
 */
export const useSpaceById = (id?: string, options: QueryHookOptions = {}) => {
  const { staleTime = 60000 } = options;

  return useQuery({
    queryKey: spacesQueryKeys.space(id),
    queryFn: async () => getSpaceById(id),
    staleTime,
    enabled: true,
  });
};

/**
 * Hook to fetch users in a space
 */
export const useSpaceUsers = (spaceId: string | undefined, options: QueryHookOptions = {}) => {
  const { staleTime = 60000 } = options;

  return useQuery<SpaceUser[]>({
    queryKey: spacesQueryKeys.users(spaceId),
    queryFn: () => fetchSpaceUsers(spaceId),
    staleTime,
    enabled: !!spaceId,
  });
};

/**
 * Hook to fetch space invoker
 */
export const useSpaceInvoker = (spaceId: string | undefined, options: QueryHookOptions = {}) => {
  const { staleTime = 60000 } = options;

  return useQuery<Invoker>({
    queryKey: spacesQueryKeys.invoker(spaceId),
    queryFn: async () => {
      const space = await getSpaceById(spaceId);
      return space?.invoker;
    },
    staleTime,
    enabled: !!spaceId,
  });
};

/**
 * Hook to fetch space groups
 */
export const useSpaceGroups = (spaceId: string | undefined, options: QueryHookOptions = {}) => {
  const { staleTime = 60000 } = options;

  return useQuery({
    queryKey: spacesQueryKeys.groups(spaceId),
    queryFn: async () => getSpaceGroups(spaceId),
    staleTime,
    enabled: true,
  });
};

/**
 * Hook to fetch group users
 */
export const useGroupUsers = (groupId: string, options: QueryHookOptions = {}) => {
  const { staleTime = 60000 } = options;

  return useQuery({
    queryKey: spacesQueryKeys.groupUsers(groupId),
    queryFn: () => getGroupUsers(groupId),
    staleTime,
    enabled: true,
  });
};

/**
 * Hook to fetch notifications data
 */
export const useNotificationsData = () => {
  return useQuery({
    queryKey: spacesQueryKeys.notifications,
    queryFn: getAllNotifications,
    refetchInterval: conf.isNotificationsPollingEnabled ? 3000 : false,
    enabled: conf.isNotificationsPollingEnabled,
  });
};

/**
 * Alias for notifications data hook
 */
export const useSpaceNotifications = () => useNotificationsData();

/**
 * Hook for updating a space
 */
export const useUpdateSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSpace,
    onError: (err) => {
      notification.error({
        message: 'Error',
        description: err?.message || "We couldn't update the space",
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data, variables) => {
      const { id } = variables;

      // Invalida queries relacionadas
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.space(id) });

      notification.success({
        message: 'Success',
        description: 'Edited successfully, this space has been updated',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for creating a space
 */
export const useCreateSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSpace,
    onError: (err) => {
      notification.error({
        message: 'Error',
        description: err?.message || "We couldn't create the space",
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message bold-notification-message',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.all });
      notification.success({
        message: 'Success',
        description: 'Space created successfuly',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message bold-notification-message',
      });
    },
  });
};

/**
 * Hook for deleting a space
 */
export const useDeleteSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSpace,
    onError: () => {
      notification.error({
        message: 'Error',
        description: 'Could not delete the space.',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.all });

      notification.success({
        message: 'Success',
        description: 'Space deleted successfully!',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for adding users to a space
 */
export const useAddUsersToSpace = (spaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (users: UserInvite[]) => {
      const responses = await Promise.allSettled(users.map((user) => inviteUser(user)));
      const errors = responses.filter((r) => r.status === 'rejected').map((r) => r.reason);
      const success = responses.filter((r) => r.status === 'fulfilled').map((r) => r.value);

      errors.forEach((e) => {
        notification.error({
          message: 'Error',
          description: e.message,
          pauseOnHover: true,
          className: 'custom-error-notifications bold-notification-message',
        });
      });

      if (success.length) {
        queryClient.invalidateQueries({ queryKey: spacesQueryKeys.users(spaceId) });
      }

      for (const s of success) {
        notification.success({
          message: 'Success',
          description: s,
          pauseOnHover: true,
          className: 'custom-success-notifications bold-notification-message',
        });
      }
      return responses;
    },
  });
};

/**
 * Hook for accepting an invitation
 */
export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acceptInvite,
    onError: () => {
      notification.error({
        message: 'Error',
        description: 'An error occurred while accepting the invitation to space',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      // Invalida queries relacionadas
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.all });

      notification.success({
        message: 'Success',
        description: 'Invitation accepted successfully',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for creating a group
 */
export const useCreateGroup = (spaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createGroup,
    onError: (error: any) => {
      notification.error({
        message: 'Error',
        description: `An error occurred while creating group. ${error.response.data.error.Data}`,
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      // Invalida queries relacionadas
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.groups(spaceId) });

      notification.success({
        message: 'Success',
        description: 'Group created successfully',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for removing a user from a group
 */
export const useRemoveUserFromGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeUserFromGroup,
    onError: () => {
      notification.error({
        message: 'Error',
        description: 'An error occured while creating group.',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      notification.success({
        message: 'Success',
        description: 'Group created successfully',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for adding users to a group
 */
export const useAddUsersToGroup = (spaceId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addGroupUsers,
    onError: () => {
      notification.error({
        message: 'Error',
        description: 'An error occured while adding users to the group.',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.groups(spaceId) });
      notification.success({
        message: 'Success',
        description: 'Users added to the group successfully.',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for updating a space user
 */
export const useUpdateSpaceUser = (spaceId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSpaceUser,
    onError: (error) => {
      notification.error({
        message: 'Error',
        description: error.message || 'An error occurred while updating user',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      // Invalida queries relacionadas
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.users(spaceId) });
      notification.success({
        message: 'Success',
        description: 'User successfully updated',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for removing a space user
 */
export const useRemoveSpaceUser = (spaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeSpaceUser,
    onError: (error) => {
      notification.error({
        message: 'Error',
        description: error.message || 'An error occurred while deleting user',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      // Invalida queries relacionadas

      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.users(spaceId) });
      notification.success({
        message: 'Success',
        description: 'User successfully deleted',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for updating a group
 */
export const useUpdateGroup = (spaceId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGroup,
    onError: (error) => {
      notification.error({
        message: 'Error',
        description: error.message || 'An error occurred while updating group',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      // Invalida queries relacionadas

      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.groups(spaceId) });
      notification.success({
        message: 'Success',
        description: 'Group successfully updated',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for deleting a group
 */
export const useDeleteGroup = (spaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteGroup,
    onError: (error) => {
      notification.error({
        message: 'Error',
        description: error.message || 'An error occurred while deleting group',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      // Invalida queries relacionadas

      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.groups(spaceId) });
      notification.success({
        message: 'Success',
        description: 'Group successfully deleted',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for deleting a group user
 */
export const useDeleteGroupUser = (spaceId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteGroupUser,
    onError: (error) => {
      notification.error({
        message: 'Error',
        description: error.message || 'An error occurred while deleting group user.',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.groups(spaceId) });
      notification.success({
        message: 'Success',
        description: 'Group user successfully deleted',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * Hook for canceling an invitation
 */
export const useCancelInvitation = (spaceId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelInvitation,
    onError: (error) => {
      notification.error({
        message: 'Error',
        description: error.message || 'An error occurred while canceling user invite.',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: spacesQueryKeys.users(spaceId) });
      notification.success({
        message: 'Success',
        description: 'Invitation successfully canceled',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

// Helper hooks
export const useNotificationAlerts = () => {
  const previousNotificationsRef = useRef<any[]>([]);
  const isInitializedRef = useRef(false);

  const query = useNotificationsData();

  useEffect(() => {
    if (!query.data) {
      return;
    }

    if (!isInitializedRef.current) {
      previousNotificationsRef.current = [...query.data];
      isInitializedRef.current = true;
      return;
    }

    const previousIds = new Set(previousNotificationsRef.current.map((n) => n.id));
    const newNotifications = query.data.filter((n: { id: string; message: string }) => !previousIds.has(n.id));

    // ✅ Mostrar solo notificaciones nuevas
    if (newNotifications.length > 0) {
      newNotifications.forEach((newNotification: { id: string; message: string }, index: number) => {
        // ✅ Mostrar notificación inmediatamente
        setTimeout(() => {
          notification.info({
            message: 'New notification received!',
            description: newNotification.message || 'You have a new notification. Check your notifications panel for details.',
            pauseOnHover: true,
            duration: 5,
            className: 'custom-info-notifications bold-notification-message',
            key: `new-notif-${newNotification.id}`,
          });
        }, index * 200);
      });
    }

    previousNotificationsRef.current = [...query.data];
  }, [query.data]);

  return { isActive: true };
};

// Types
interface UserInvite {
  space_id: string;
  expiration: string;
  email: string;
  roles: string[];
  clearance: string;
}

// SpaceUser is now defined and exported from spaces.api.ts

/**
 * Interface representing the structure of an Invoker object.
 */
export interface Invoker {
  clearance: string;
  color: string;
  creation_date: string;
  doc_type: string;
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  last_tx_time: string;
}
